import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import type { ConversationDTO } from '@/types';
import { useRealtime } from '@/providers/RealtimeProvider';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';

export function useUnreadMessageCount(activeTab: string): number {
  const [unreadCount, setUnreadCount] = useState(0);
  const { subscribeNewMessageBackground } = useRealtime();
  const { userRole } = useCurrentUser();
  const { selectedCompanyId, isLoading: companyLoading } = useGlobalCompany();
  const queryClient = useQueryClient();

  const senderSide = userRole?.toLowerCase() === 'technician' ? 'Technician' : 'Company';

  const fetchUnreadCount = useCallback(async () => {
    const conversationsKey = ['conversations', selectedCompanyId] as const;
    const sumUnread = (items: ConversationDTO[]) =>
      items.reduce((sum, c) => sum + c.unread_count, 0);
    try {
      const response = await apiService.getConversations(1, 200);
      setUnreadCount(sumUnread(response.items));
      queryClient.setQueryData(conversationsKey, response.items);
    } catch {
      // Offline: fall back to the persisted conversation cache so the badge
      // still reflects last-known unread counts.
      const cached = queryClient.getQueryData<ConversationDTO[]>(conversationsKey);
      if (cached) setUnreadCount(sumUnread(cached));
    }
  }, [queryClient, selectedCompanyId]);

  // `undefined` = never fetched; `null` = fetched with no company selected.
  const fetchedCompanyRef = useRef<string | null | undefined>(undefined);
  const prevTabRef = useRef<string | null>(null);

  // Fetch once selectedCompanyId is ready, and on company change or leaving messaging tab.
  //
  // activeTab has to stay a dependency (we need to know when messaging is left), but without
  // the guards below every jobs -> calendar -> map hop re-fetched up to 200 conversation DTOs
  // just to re-sum one integer — which the realtime subscription further down already keeps
  // current. Those are the only two moments a real fetch tells us something new.
  useEffect(() => {
    if (companyLoading || !selectedCompanyId) return;

    const prevTab = prevTabRef.current;
    prevTabRef.current = activeTab;

    // On the messaging tab the list itself is authoritative and the badge is zeroed below.
    if (activeTab === 'messaging') return;

    const isNewCompany = fetchedCompanyRef.current !== selectedCompanyId;
    const leftMessaging = prevTab === 'messaging';
    if (!isNewCompany && !leftMessaging) return;

    fetchedCompanyRef.current = selectedCompanyId;
    fetchUnreadCount();
  }, [companyLoading, selectedCompanyId, activeTab, fetchUnreadCount]);

  // Reset when entering messaging tab (conversations get marked as read there)
  useEffect(() => {
    if (activeTab === 'messaging') {
      setUnreadCount(0);
    }
  }, [activeTab]);

  // Subscribe to real-time new message events (background — does not suppress toast)
  useEffect(() => {
    const unsubscribe = subscribeNewMessageBackground((event) => {
      if (event.sender_type !== senderSide && activeTab !== 'messaging') {
        setUnreadCount((prev) => prev + 1);
      }
    });
    return unsubscribe;
  }, [subscribeNewMessageBackground, senderSide, activeTab]);

  return unreadCount;
}
